import React, { useMemo } from 'react'
import classNames from 'classnames'
import format from 'date-fns/format'
import Link from 'next/link'
import { Type } from '@/components/common'
import { CheckCircle, Exclamation } from '@/components/svg'
import { IVA } from '@/services/api/IVA'
import { Campaign } from '@/services/api/campaign'
import { Investment } from '@/services/api/investment/types'
import { Payout } from '@/services/api/payout'
import { formatMoney } from '@/utils'
import UserCampaignCard from './UserCampaignCard'

export interface Props {
  campaign: Campaign
  investment: Investment
  IVA?: IVA
  payout?: Payout
  className?: string
}

const UserInvestmentCard: React.FC<Props> = ({
  campaign,
  investment,
  IVA,
  payout,
  className,
}) => {
  const hasPaymentIssue = useMemo(
    () =>
      ['status_fail_non_sufficient_funds', 'status_fail_bad_account'].includes(
        investment.paymentStatus
      ) &&
      !investment.canceled &&
      campaign.withinPaymentUpdateWindow,
    [investment, campaign]
  )

  const status = useMemo(() => {
    if (investment.canceled) {
      return 'Investment cancelled'
    }

    const actionRequired =
      hasPaymentIssue || // payment issue
      IVA?.status === 'Pending User Action' || // unresolved IV
      investment.requiresReconfirmation || // needs reconfirmed
      (campaign.supportTipaltiPayback && // investment complete and no payout info yet
        investment.completed &&
        !investment.isTipaltiPayable)

    if (actionRequired) {
      return (
        <>
          <Exclamation className="mr-[2px] h-4 w-4 text-red" />
          <span className="text-red">Action Required</span>
        </>
      )
    }

    // Since closed IVAs are not returned by the api, any non-user-action IVA
    // means that we're waiting on a review
    if (IVA) {
      return 'Pending approval'
    }

    if (payout) {
      return (
        <>
          <CheckCircle className="mr-[2px] h-4 w-4 text-oxide" />
          <span className="font-semibold text-oxide">
            Paid out {format(new Date(payout.payoutDate), 'MMMM d, yyyy')}
          </span>
        </>
      )
    }

    if (investment.completed) {
      return (
        <>
          <CheckCircle className="mr-[2px] h-4 w-4 text-oxide" />
          <span>Investment completed</span>
        </>
      )
    }

    return 'Investment incomplete'
  }, [investment, campaign, IVA, payout, hasPaymentIssue])

  return (
    <Link
      href={
        investment.completed || hasPaymentIssue
          ? `/investments/${investment.id}`
          : `/invest/${campaign.slug}/${investment.id}`
      }
      className={classNames(
        'active:opacity-75',
        { 'pointer-events-none': investment.canceled },
        className
      )}
    >
      <UserCampaignCard
        title={campaign.name}
        subtitle={format(
          new Date(investment.closedAt || investment.modified),
          'MMMM d, yyyy'
        )}
        description={status}
        rightContent={
          <>
            {investment.amount > 0 && (
              <Type
                as="h3"
                variant="title-xs"
                className="mb-[3px] font-semibold"
              >
                {formatMoney(investment.amount)}
              </Type>
            )}

            {investment.shares > 0 && (
              <Type as="p" variant="caption-sm" className="text-gray">
                {investment.shares.toLocaleString()} units
              </Type>
            )}
          </>
        }
        campaignSlug={campaign.slug}
        className="items-start rounded-lg bg-white p-3 shadow-light"
      />
    </Link>
  )
}

export default UserInvestmentCard
